
import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import PrivacyPolicyModal from '../../../components/feature/PrivacyPolicyModal';

export default function Footer() {
  const { ref: footerRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    try {
      const body = new URLSearchParams();
      body.append('email', email);

      const res = await fetch('https://readdy.ai/api/form/d64onb49vs2s8262n48g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubStatus('success');
        setEmail('');
      } else {
        setSubStatus('error');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setSubStatus('error');
    }
  };

  return (
    <footer ref={footerRef} className="bg-dark py-12 sm:py-16 px-4 sm:px-8 lg:px-16 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
          <div 
            className={`transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold rounded-md flex items-center justify-center">
                <span className="font-heading text-2xl font-bold text-dark tracking-wider">AG</span>
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-lg font-bold text-white tracking-wide">GA HR Group</span>
                <span className="font-body text-xs text-white/60 -mt-0.5">HR-бизнес-партнёр</span>
              </div>
            </div>
            <p className="font-body text-xs text-white/40 leading-relaxed">
              GA HR Group — HR‑бизнес‑партнёр. Создаём системы управления персоналом с нуля.
            </p>
          </div>

          <div 
            className={`transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: isVisible ? '200ms' : '0ms' }}
          >
            <h4 className="font-body text-sm font-semibold text-white mb-4 sm:mb-6">
              <a href="#services">Услуги</a>
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  Пакет «СТРАТЕГ»
                </a>
              </li>
              <li>
                <a href="#services" className="font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  Пакет «ЗАПУСК»
                </a>
              </li>
              <li>
                <a href="#services" className="font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  Пакет «ПОДБОР»
                </a>
              </li>
              <li>
                <a href="#services" className="font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  Пакет «ОПТИМИЗАЦИЯ»
                </a>
              </li>
            </ul>
          </div>

          <div 
            className={`transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: isVisible ? '300ms' : '0ms' }}
          >
            <h4 className="font-body text-sm font-semibold text-white mb-4 sm:mb-6">
              <a href="#contact">Контакты</a>
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="https://tenchat.ru/angel-hr" rel="nofollow" target="_blank" className="flex items-center gap-2 font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-telegram-line"></i></div>
                  Tenchat‑профиль
                </a>
              </li>
              <li>
                <a href="tel:+79912967892" rel="nofollow" className="flex items-center gap-2 font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></div>
                  +7 (991) 296-78-92
                </a>
              </li>
              <li>
                <a href="mailto:info@gahrgroup.com" rel="nofollow" className="flex items-center gap-2 font-body text-xs text-white/40 hover:text-gold transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line"></i></div>
                  Email
                </a>
              </li>
            </ul>
          </div>

          <div 
            className={`transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: isVisible ? '400ms' : '0ms' }}
          >
            <h4 className="font-body text-sm font-semibold text-white mb-4 sm:mb-6">Рассылка</h4>
            <p className="font-body text-xs text-white/40 mb-4">Полезные материалы по HR</p>
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubscribe}
              className="flex items-center gap-2"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ваш email"
                required
                className="flex-1 min-w-0 px-4 py-3 bg-transparent border border-white/15 rounded-md font-body text-xs text-white placeholder-white/30 focus:outline-none focus:border-gold/60 transition-colors"
              />
              <button
                type="submit"
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-gold rounded-md hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-dark text-sm"></i>
              </button>
            </form>
            {subStatus === 'success' && (
              <p className="font-body text-xs text-green-400 mt-2">Вы подписаны!</p>
            )}
            {subStatus === 'error' && (
              <p className="font-body text-xs text-red-400 mt-2">Ошибка. Попробуйте ещё раз.</p>
            )}
          </div>
        </div>

        <div 
          className={`border-t border-white/10 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: isVisible ? '500ms' : '0ms' }}
        >
          <p className="font-body text-xs text-white/30">© 2025 GA HR Group. Все права защищены.</p>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
            <button
              onClick={() => setIsPolicyOpen(true)}
              className="font-body text-xs text-white/30 hover:text-gold transition-colors cursor-pointer"
            >
              Политика конфиденциальности
            </button>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-xs text-white/30 hover:text-white/50 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>

      <PrivacyPolicyModal isOpen={isPolicyOpen} onClose={() => setIsPolicyOpen(false)} />
    </footer>
  );
}
